import { useGetSubstitutabilityQuery } from 'store/services/core/substitutability';
import { memo, useMemo } from 'react';
import { DropdownMenuItem } from './DropdownMenuItem';
import { isPathActive } from './isPathActive';
import { useTranslation } from 'react-i18next';
import { useLogin } from 'contexts';
import { getAdminAccessFlags } from 'utils/adminAccess';
import { useLocation } from 'react-router-dom';

import type { IconNames } from '../Icon';

export const AdminMenu = memo(({ open }: { open: boolean }) => {
  const { t } = useTranslation();
  const { permissions } = useLogin();
  const { data } = useGetSubstitutabilityQuery();
  const { pathname } = useLocation();

  const menuItemsData = useMemo(() => {
    const access = getAdminAccessFlags(permissions);
    return [
      {
        name: t('sidebar.admin.flow._'),
        icon: 'SouthEast',
        path: 'flow',
        dataTestId: 'admin-sidebar-section-flow',
        links: access.flow
          ? [
              {
                name: t('sidebar.admin.flow.modules&agendas._'),
                path: '/admin/flow/modules&agendas',
              },
              {
                name: t('sidebar.admin.flow.tables._'),
                path: '/admin/flow/tables',
              },
              {
                name: t('sidebar.admin.flow.notification'),
                path: '/admin/flow/notification',
              },
              {
                name: t('sidebar.admin.flow.restApiConnections'),
                path: '/admin/flow/rest-api-connections',
              },
              {
                name: t('sidebar.admin.flow.agendaImports._'),
                path: '/admin/flow/agenda-imports',
              },
            ]
          : [],
      },
      {
        name: t('sidebar.admin.helios._'),
        icon: 'HMobiledata',
        path: 'helios',
        dataTestId: 'admin-sidebar-section-helios',
        links: access.helios
          ? [
              {
                name: t('sidebar.admin.helios.tables'),
                path: '/admin/helios/tables',
              },
              {
                name: t('sidebar.admin.helios.templates'),
                path: '/admin/helios/templates',
              },
              {
                name: t('sidebar.admin.helios.dataSource'),
                path: '/admin/helios/data-source',
              },
              {
                name: t('sidebar.admin.helios.functions'),
                path: '/admin/helios/functions',
              },
              {
                name: t('sidebar.admin.helios.notification'),
                path: '/admin/helios/notification',
              },
              {
                name: t('sidebar.admin.helios.templatesImport'),
                path: '/admin/helios/templates-import',
              },
            ]
          : [],
      },
      {
        name: t('sidebar.admin.core._'),
        icon: 'ControlCamera',
        path: 'core',
        dataTestId: 'admin-sidebar-section-core',
        links: [
          ...(access.coreUsers
            ? [
                {
                  name: t('sidebar.admin.core.users'),
                  path: '/admin/core/users',
                },
              ]
            : []),
          ...(access.coreModules
            ? [
                {
                  name: t('sidebar.admin.core.modules'),
                  path: '/admin/core/modules',
                },
              ]
            : []),
          ...(access.coreAssistants
            ? [
                {
                  name: t('sidebar.admin.core.assistants'),
                  path: '/admin/core/assistants',
                },
              ]
            : []),
          ...(access.coreRoles
            ? [
                {
                  name: t('sidebar.admin.core.roles'),
                  path: '/admin/core/roles',
                },
              ]
            : []),
          ...(access.coreSubstitutability && data && !('error' in data)
            ? [
                {
                  name: t('sidebar.admin.core.substitutability._'),
                  path: '/admin/core/substitutability',
                },
              ]
            : []),
          ...(access.coreSettings
            ? [
                {
                  name: t('sidebar.admin.core.appSettings'),
                  path: '/admin/core/app-settings',
                },
              ]
            : []),
        ],
      },
    ];
  }, [permissions, data, t]);

  const renderedItems = useMemo(() => {
    return menuItemsData
      .filter((item) => item.links.length > 0)
      .map((item) => (
        <DropdownMenuItem
          key={item.path}
          open={open}
          path={item.path}
          text={item.name}
          icon={item.icon as IconNames}
          links={item.links}
          dataTestId={item.dataTestId}
          pathname={pathname}
          active={isPathActive(pathname, item.path)}
        />
      ));
  }, [menuItemsData, open, pathname]);

  return <>{renderedItems}</>;
});
